import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Stack,
  Typography,
  Box,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Menu,
  Modal,
  Skeleton,
} from "@mui/material";

import LayerdIcon from "../../../../common/LayerdIcon";
import { UI_ICONS } from "../../../../../icons/uiLucideIcons";
import { ReactComponent as LoadingIcon } from "../../../../../vendor/_new/icon/loading.svg";

import { GreenSelectForm, GreenSwitch } from "../../../../common/Greens";
import colors from "../../../../../utils/colors";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import { tableHeadSx, tableBodySx } from "../../detection/WAFEvent";
import { getExpressionKeyTitle } from "../../../../../utils/constants";
import { FirewallAction } from "../../../../../utils/constants";
import { formatDate } from "../../../../../utils/format";
import { generateMenuSx } from "../../../../../layouts/_new/Normal";
import { getRateLimitCharacteristicTitle } from "../../../../../utils/constants";
import { getHumanTimeLength } from "../../../../../utils/format";
import RateLimitingRuleModal from "./RateLimitingRuleModal";
import { CancelButton, GhostButton, PrimaryButton, SaveButton } from "../../../../common/Button";
import { useNotification } from "../../../../../contexts/NotificationContext";
import StickySaveBar from "../../../../common/StickySaveBar";
import { cloneItems, collectRuleEnabledDiff } from "../../../../common/stickySaveBarUtils";

import useAuth from "../../../../../hooks/useAuth";
import usePermissions from "../../../../../hooks/usePermissions";
import useSite from "../../../../../hooks/user/useSite";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import useRateLimit from "../../../../../hooks/user/useRateLimit";

const getAction = (code) => {
  switch (code) {
    case FirewallAction.CHALLENGE:
      return "CHALLENGE";
    case FirewallAction.BLOCK:
      return "BLOCK";
    case FirewallAction.DROP:
      return "DROP";
    default:
      break;
  }
};

const toolbarGhostButtonSx = {
  height: 40,
  minHeight: 40,
  minWidth: 112,
  px: 2,
  fontSize: 14,
  lineHeight: "21px",
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": { mr: 1 },
};

const toolbarPrimaryButtonSx = {
  height: 40,
  minHeight: 40,
  minWidth: 180,
  px: 2,
  py: 0,
  fontSize: 14,
  lineHeight: "21px",
  fontWeight: 400,
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": { mr: 1 },
};

const NewServicesRateLimitingRules = () => {
  const { isAuthenticated, userType } = useAuth();
  const { hasPermission, PERMISSIONS } = usePermissions();
  const { getRateLimits, updateRateLimit, ratelimits, deleteRateLimit, errMsg, setErr } = useRateLimit();
  const { getSitesForItems } = useSite();
  const { showSuccess, showError } = useNotification();
  
  // Check permissions
  const canWriteRateLimit = hasPermission(PERMISSIONS.RATE_LIMIT_WRITE);
  const canDeleteRateLimit = hasPermission(PERMISSIONS.RATE_LIMIT_DELETE);

  const { effectiveSiteId, effectiveSiteUid } = useGlobalSite();
  const siteID = effectiveSiteId ?? "";
  const siteUid = effectiveSiteUid;
  const [savedRateLimits, setSavedRateLimits] = useState(null);
  const [workingRateLimits, setWorkingRateLimits] = useState(null);

  useEffect(() => {
    if (ratelimits) {
      setSavedRateLimits(ratelimits);
      setWorkingRateLimits(ratelimits);
    }
  }, [ratelimits]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getSitesForItems();
    }
    return () => setErr(null);
  }, [isAuthenticated, getSitesForItems, setErr]);

  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSavingToggles, setIsSavingToggles] = useState(false);

  useEffect(() => {
    if (siteUid) {
      setLoading(true);
      setWorkingRateLimits(null);
      getRateLimits(siteUid, true).then(() => setLoading(false));
    }
  }, [siteUid, getRateLimits]);

  useEffect(() => {
    if (errMsg) {
      showError(errMsg);
    }
  }, [errMsg, showError]);

  const [rateLimitRuleToEdit, setRateLimitRuleToEdit] = useState(null);
  const [openRateLimitRule, setOpenRateLimitRule] = useState(false);
  const handleRateLimitRuleOpen = () => setOpenRateLimitRule(true);
  const handleRateLimitRuleClose = () => {
    setOpenRateLimitRule(false);
    setRateLimitRuleToEdit(null);
  };

  const [actionMenu, setActionMenu] = useState({
    anchorEl: null, // the DOM element the menu anchors to
    rateLimitRuleId: null, // index of subrow (or null if main row)
  });
  // open handler (call from the ActionIcon onClick)
  const handleActionOpen = (event, rateLimitRuleId = null) => {
    event.stopPropagation(); // prevent row expand/click side effects
    setActionMenu({ anchorEl: event.currentTarget, rateLimitRuleId });
  };

  // close handler
  const handleActionClose = () => {
    setActionMenu({ anchorEl: null, rateLimitRuleId: null });
  };

  const isActionMenuOpen = Boolean(actionMenu.anchorEl);

  const [deleteRateLimitId, setDeleteRateLimitId] = useState();
  const [openRateLimitRuleToDelete, setOpenRateLimitRuleToDelete] = useState(false);
  const handleRateLimitRuleToDeleteOpen = () => setOpenRateLimitRuleToDelete(true);
  const handleRateLimitRuleToDeleteClose = () => setOpenRateLimitRuleToDelete(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteRateLimit(siteUid, { ratelimit_rule_id: deleteRateLimitId });
    } catch (err) {
      console.error(err);
    }
    handleRateLimitRuleToDeleteClose();
    setLoading(false);
  };

  const stickyModifiedFields = useMemo(
    () => collectRuleEnabledDiff(savedRateLimits, workingRateLimits),
    [savedRateLimits, workingRateLimits],
  );
  const stickyModifiedCount = stickyModifiedFields.length;

  const changeRateLimitEnable = (event, id) => {
    if (!canWriteRateLimit || isSavingToggles) return;
    const enabled = event.target.checked;
    setWorkingRateLimits((prev) =>
      (prev || []).map((item) => (item?.id === id ? { ...item, enabled } : item)),
    );
  };

  const discardToggleChanges = useCallback(() => {
    setWorkingRateLimits(cloneItems(savedRateLimits));
  }, [savedRateLimits]);

  const applyToggleChanges = useCallback(async () => {
    if (!canWriteRateLimit || !siteUid || stickyModifiedCount === 0) return;
    const savedById = Object.fromEntries((savedRateLimits || []).map((r) => [r.id, !!r.enabled]));
    const changed = (workingRateLimits || []).filter((r) => savedById[r.id] !== !!r.enabled);
    if (changed.length === 0) return;

    setIsSavingToggles(true);
    try {
      const results = await Promise.all(
        changed.map((r) => updateRateLimit(siteUid, r.id, { enabled: !!r.enabled })),
      );
      const failed = results.find((r) => r?.status !== "success");
      if (failed) {
        showError(failed?.msg || "Failed to update rate limit rule");
        return;
      }
      setSavedRateLimits(cloneItems(workingRateLimits));
      showSuccess(`Saved ${changed.length} rate limit rule${changed.length === 1 ? "" : "s"}.`);
    } catch (err) {
      showError(err?.message || "Failed to update rate limit rules");
    } finally {
      setIsSavingToggles(false);
    }
  }, [
    canWriteRateLimit,
    siteUid,
    stickyModifiedCount,
    savedRateLimits,
    workingRateLimits,
    updateRateLimit,
    showError,
    showSuccess,
  ]);

  const refresh = async () => {
    if (siteUid) {
      setIsRefreshing(true);
      setLoading(true);
      await getRateLimits(siteUid, true);
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  return (
    <>
      <Helmet title="Rate Limiting Rules" />
      <Stack spacing={3} sx={{ p: 6 }}>
        <V2PageHeader
          title="Rate Limiting Rules"
          helpTooltip="Define rate-limiting policies by URL pattern, method, or header. Exceeded limits can block, challenge, or log requests to protect origin capacity."
          sx={{ pb: 0, alignItems: "center" }}
          actions={
            siteID ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <GhostButton
                  disabled={isRefreshing || loading}
                  startIcon={
                    isRefreshing ? (
                      <LoadingIcon style={{ color: colors.neutral[600], width: 16, height: 16 }} />
                    ) : (
                      <LayerdIcon icon={UI_ICONS.refresh} size="sm" color="neutral" />
                    )
                  }
                  onClick={refresh}
                  sx={toolbarGhostButtonSx}
                >
                  Refresh
                </GhostButton>
                {canWriteRateLimit && (
                  <PrimaryButton
                    startIcon={<LayerdIcon icon={UI_ICONS.plus} size="sm" color="inherit" />}
                    onClick={() => {
                      setRateLimitRuleToEdit(null);
                      handleRateLimitRuleOpen();
                    }}
                    sx={toolbarPrimaryButtonSx}
                  >
                    Add rate limiting rule
                  </PrimaryButton>
                )}
              </Stack>
            ) : null
          }
        />
        <SiteScopeGate mode="single">
          {() => (
          <Box sx={{ width: "100%", borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, p: 2 }}>
            <Table
              sx={{
                "& .MuiTableCell-head": { py: 2, px: 3 },
                "& .MuiTableCell-body": { py: 1.5, px: 3 },
                "& .MuiTableCell-root": { borderBottom: "none" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography sx={tableHeadSx}>No</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Name</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Action</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Characteristics</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Mitigation timeout</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Period</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Requests per period</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Timestamp</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Status</Typography>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workingRateLimits === null &&
                  [1, 2, 3, 4, 5]?.map((index) => (
                    <TableRow key={`ske_${index}`}>
                      <TableCell>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell colSpan={2}>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                    </TableRow>
                  ))}
                {workingRateLimits?.map((row, index) => (
                  <TableRow key={`rateLimit_rule_${index}`}>
                    <TableCell>
                      <Typography sx={tableBodySx}>{index + 1}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{row?.name ? row?.name : row?.id}</Typography>
                      <Typography sx={{ ...tableBodySx, color: colors.neutral[600] }}>
                        {row?.condition_fields && row?.condition_fields.map((key) => getExpressionKeyTitle(key)).join(", ")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{getAction(row?.action)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>
                        {row?.characteristic_fields &&
                          row?.characteristic_fields.map((key) => getRateLimitCharacteristicTitle(key)).join(", ")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{getHumanTimeLength(row?.mitigation_timeout)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{getHumanTimeLength(row?.period)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{row?.requests_per_period}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{formatDate(row?.created_at) || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <GreenSwitch
                        checked={row?.enabled}
                        disabled={!canWriteRateLimit || isSavingToggles}
                        onChange={(e) => changeRateLimitEnable(e, row?.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack alignItems={"flex-end"} sx={{ width: "100%" }}>
                        {(canWriteRateLimit || canDeleteRateLimit) && (
                          <Box onClick={(e) => handleActionOpen(e, row?.id)} sx={{ cursor: "pointer", width: 24, height: 24 }}>
                            <LayerdIcon icon={UI_ICONS.more} size={18} color="neutral" />
                          </Box>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {workingRateLimits !== null && workingRateLimits?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 3, borderBottom: "none" }}>
                      <Typography sx={{ ...tableBodySx, textAlign: "center" }}>
                        There are no rate limiting rules
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          )}
        </SiteScopeGate>
      </Stack>
      <Menu
        anchorEl={actionMenu.anchorEl}
        open={isActionMenuOpen}
        onClose={handleActionClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
            width: 200,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          {canWriteRateLimit && (
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={1}
              onClick={() => {
                const { rateLimitRuleId } = actionMenu;
                setRateLimitRuleToEdit(workingRateLimits?.find((item) => item?.id === rateLimitRuleId));
                handleRateLimitRuleOpen();
                handleActionClose();
              }}
              sx={generateMenuSx(176, 37)}
            >
              <LayerdIcon icon={UI_ICONS.edit} size={18} color="neutral" />
              <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>Edit</Typography>
            </Stack>
          )}
          {canDeleteRateLimit && (
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={1}
              onClick={() => {
                const { rateLimitRuleId } = actionMenu;
                setDeleteRateLimitId(rateLimitRuleId);
                handleRateLimitRuleToDeleteOpen();
                handleActionClose();
              }}
              sx={generateMenuSx(176, 37)}
            >
              <LayerdIcon icon={UI_ICONS.delete} size={18} color="neutral" />
              <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                Delete
              </Typography>
            </Stack>
          )}
        </Box>
      </Menu>
      <Modal open={openRateLimitRuleToDelete} onClose={handleRateLimitRuleToDeleteClose}>
        <Stack
          spacing={4}
          sx={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: colors.neutral[100],
            borderRadius: "8px",
            p: 6,
          }}
        >
          <Stack spacing={3} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
              Delete Rate Limiting Rule?
            </Typography>
            <Box onClick={handleRateLimitRuleToDeleteClose} sx={{ cursor: "pointer" }}>
              <LayerdIcon icon={UI_ICONS.close} size={20} color="neutral" />
            </Box>
          </Stack>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            Are you sure you want to permanently delete this rateLimit rule?
          </Typography>
          <Stack direction={"row"} alignItems={"center"} spacing={4}>
            <CancelButton onClick={handleRateLimitRuleToDeleteClose} outlined={true} />
            <SaveButton onClick={handleDelete} disabled={loading} label={"Delete"} />
          </Stack>
        </Stack>
      </Modal>
      <RateLimitingRuleModal
        siteUid={siteUid}
        open={openRateLimitRule}
        handleClose={handleRateLimitRuleClose}
        rateLimitRuleToEdit={rateLimitRuleToEdit}
      />
      <StickySaveBar
        open={canWriteRateLimit && stickyModifiedCount > 0}
        modifiedCount={stickyModifiedCount}
        modifiedFields={stickyModifiedFields}
        onDiscard={discardToggleChanges}
        onApply={applyToggleChanges}
        isApplying={isSavingToggles}
      />
    </>
  );
};
export default NewServicesRateLimitingRules;
